const CACHE_VERSION = 6;
const CACHE_PREFIX = 'yk8.info-screen.resource.v6.';
const memoryCache = new Map();
const inFlight = new Map();

function getStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function storageKey(key) {
  return `${CACHE_PREFIX}${key}`;
}

function readEnvelope(key) {
  if (memoryCache.has(key)) {
    return memoryCache.get(key);
  }

  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(storageKey(key));
    const parsed = raw ? JSON.parse(raw) : null;
    const valid = parsed?.version === CACHE_VERSION && typeof parsed.savedAt === 'number' && 'data' in parsed;
    const envelope = valid ? parsed : null;
    memoryCache.set(key, envelope);
    return envelope;
  } catch {
    memoryCache.set(key, null);
    return null;
  }
}

function writeEnvelope(key, data, savedAt = Date.now()) {
  const envelope = { version: CACHE_VERSION, savedAt, data };
  memoryCache.set(key, envelope);

  try {
    getStorage()?.setItem(storageKey(key), JSON.stringify(envelope));
  } catch {
    // Storage can be disabled or full; the in-memory cache remains useful.
  }

  return envelope;
}

export function getCachedResource(key) {
  const envelope = readEnvelope(key);
  return envelope ? { data: envelope.data, updatedAt: envelope.savedAt } : null;
}

export function findCachedResource(keyPrefix, predicate = () => true) {
  const candidates = [];
  const storage = getStorage();

  if (storage) {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key?.startsWith(storageKey(keyPrefix))) {
        continue;
      }

      const resourceKey = key.slice(CACHE_PREFIX.length);
      const envelope = readEnvelope(resourceKey);
      if (envelope && predicate(envelope.data)) {
        candidates.push(envelope);
      }
    }
  }

  memoryCache.forEach((envelope, key) => {
    if (key.startsWith(keyPrefix) && envelope && predicate(envelope.data) && !candidates.includes(envelope)) {
      candidates.push(envelope);
    }
  });

  const newest = candidates.sort((a, b) => b.savedAt - a.savedAt)[0];
  return newest ? { data: newest.data, updatedAt: newest.savedAt } : null;
}

export async function loadResource({
  key,
  maxAgeMs,
  fetcher,
  signal,
  force = false,
  fallback
}) {
  const cached = readEnvelope(key);
  const isFresh = cached && Date.now() - cached.savedAt < maxAgeMs;

  if (!force && isFresh) {
    return {
      data: cached.data,
      updatedAt: cached.savedAt,
      stale: false,
      source: 'cache',
      error: null
    };
  }

  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  const promise = Promise.resolve()
    .then(() => fetcher({ signal }))
    .then((data) => {
      const envelope = writeEnvelope(key, data);
      return {
        data,
        updatedAt: envelope.savedAt,
        stale: false,
        source: 'network',
        error: null
      };
    })
    .catch((error) => {
      if (cached) {
        return {
          data: cached.data,
          updatedAt: cached.savedAt,
          stale: true,
          source: 'stale-cache',
          error
        };
      }

      return {
        data: typeof fallback === 'function' ? fallback() : fallback,
        updatedAt: null,
        stale: true,
        source: 'fallback',
        error
      };
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
}

export function storeResource(key, data, savedAt = Date.now()) {
  const envelope = writeEnvelope(key, data, savedAt);
  return { data: envelope.data, updatedAt: envelope.savedAt };
}

export function clearResourceCacheForTests() {
  memoryCache.clear();
  inFlight.clear();

  const storage = getStorage();
  if (!storage) {
    return;
  }

  const keys = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(CACHE_PREFIX)) {
      keys.push(key);
    }
  }
  keys.forEach((key) => storage.removeItem(key));
}
